import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { heroStats } from '@/mocks/landingData';

const WHATSAPP_URL = 'https://wa.me/918861133745?text=Hi%2C%20I%27m%20interested%20in%20Confident%20Auriga.%20Please%20share%20more%20details.';

export default function Hero() {
  const [imageLoaded, setImageLoaded] = useState(false);
  useScrollReveal('.hero-reveal', 0.1);

  return (
    <section id="hero" className="relative min-h-[92vh] md:min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Aerial%20drone%20photograph%20of%20a%20premium%20plotted%20land%20development%20township%20with%20neatly%20arranged%20plot%20boundaries%2C%20wide%20paved%20internal%20roads%2C%20landscaped%20green%20medians%2C%20street%20lights%2C%20and%20lush%20surrounding%20vegetation%2C%20cinematic%20high%20altitude%20perspective%2C%20warm%20golden%20hour%20lighting%2C%20organized%20grid%20pattern%20of%20empty%20land%20plots%20ready%20for%20construction%2C%20green%20and%20gold%20tones%2C%20no%20buildings%20yet%2C%20raw%20land%20development&width=1200&height=675&seq=hero-v2&orientation=landscape"
          alt="Premium plots near Bangalore - aerial view of Confident Auriga gated community plots in Malur with organized plot boundaries and wide internal roads"
          className={`w-full h-full object-cover transition-transform duration-[2s] ${imageLoaded ? 'scale-100' : 'scale-110'}`}
          onLoad={() => setImageLoaded(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/75 to-primary/90" />
        <div className="absolute inset-0 bg-[#0D2B1F]/25" />
      </div>

      <div className="relative z-10 w-full section-padding pt-24 pb-14 md:pt-36 md:pb-20">
        <div className="max-w-4xl mx-auto text-center md:text-left">
          <div className="hero-reveal scroll-reveal mb-4 md:mb-5">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white/90 font-body text-[10px] md:text-xs tracking-[0.2em] uppercase">
              <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse" />
              Premium Gated Community
            </span>
          </div>

          <h1 className="hero-reveal scroll-reveal font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white font-bold leading-[1.08] mb-4 md:mb-6">
            Premium Gated Community
            <br />
            <span className="text-gradient-gold">Plots in Malur</span>
          </h1>

          <p className="hero-reveal scroll-reveal font-body text-sm md:text-base lg:text-lg text-white/80 max-w-2xl leading-relaxed mb-6 md:mb-8 mx-auto md:mx-0">
            Secure premium investment plots in one of Karnataka&rsquo;s fastest-growing corridors. Confident Auriga in Malur offers meticulously planned gated community plots with excellent connectivity to Bangalore.
          </p>

          <div className="hero-reveal scroll-reveal max-w-[70%] mx-auto md:mx-0 mb-7 md:mb-10">
            <h3 className="font-display text-base md:text-lg text-gold font-semibold tracking-wide mb-3">
              Project Highlights
            </h3>
            <div className="font-body text-xs md:text-sm text-white/70 leading-relaxed space-y-2.5">
              <p>
                Confident Auriga is a premium 90-acre plotted development in Malur offering meticulously planned plots with modern infrastructure and excellent connectivity to Bangalore.
              </p>
              <p>
                Designed as a premium gated community with 1100+ villa plots, the project features world-class amenities, landscaped gardens, wide internal roads, 24/7 security, clubhouse facilities, and dedicated green spaces.
              </p>
              <p>
                Whether you are looking for villa plots in Malur or a strategic long-term land investment near Bangalore, Confident Auriga delivers exceptional lifestyle value and future appreciation potential.
              </p>
            </div>
          </div>

          <div className="hero-reveal scroll-reveal flex flex-col sm:flex-row gap-3 md:gap-4 mb-10 md:mb-16 justify-center md:justify-start">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary rounded-full hover:scale-[1.02] active:scale-[0.98]"
            >
              <span className="whitespace-nowrap">Book Site Visit</span>
              <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <i className="ri-arrow-right-line text-white" />
              </span>
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/10 backdrop-blur-md border border-white/30 text-white font-body font-semibold text-sm tracking-wider uppercase rounded-full transition-all duration-500 hover:bg-white hover:text-primary hover:scale-[1.02] active:scale-[0.98]"
            >
              <i className="ri-whatsapp-line text-lg" />
              <span className="whitespace-nowrap">WhatsApp Enquiry</span>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-4 max-w-4xl mx-auto">
          {heroStats.map((stat, index) => (
            <div
              key={stat.label}
              className="hero-reveal scroll-reveal glass-card rounded-xl p-3.5 md:p-5 text-center shadow-lg shadow-black/10"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="font-display text-xl md:text-3xl lg:text-4xl text-gold font-bold mb-1">
                {stat.value}
              </div>
              <div className="font-body text-[10px] md:text-sm text-white/90 tracking-wider uppercase">
                {stat.suffix || stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/50 hover:text-white transition-colors duration-500">
        <span className="font-body text-xs tracking-[0.2em] uppercase">Scroll</span>
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
          <div className="w-1 h-2 bg-gold rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
}